import { createSelector } from 'reselect'

import { getRangeFromDateFilter } from 'utils/dateUtils'
import { ALL_COMPANIES_OPTION } from 'utils/partnerUtils'
import permManager from 'utils/permManager'
import { atLeastIronscalesProtectPlan } from 'utils/plans'

import {
  selectActualFeatureFlags,
  selectActualIntegrations,
  selectActualLicenses,
  selectActualPermissions,
  selectCompanySelectorObject,
  selectIsStaticSalesDemo,
  selectPartner,
  selectSelfCompanyId,
  selectSortedCompaniesOptions,
} from 'containers/App/selectors'
import { selectCompanyFilter } from 'containers/PartnerOptions/selectors'

import { prepareBool, prepareCompanyIds } from './utils'

const mutateFiltersToOptions = (filters) => {
  const mutatedFilters = Object.keys(filters).reduce((acc, filter) => {
    acc[filter] = filters[filter].map((f) => ({
      key: f,
      value: f,
      label: f,
    }))
    return acc
  }, {})
  return mutatedFilters
}

const mutateFiltersToLabels = (filters) => {
  const mutatedFilters = Object.keys(filters).reduce((acc, filter) => {
    acc[filter] = Array.isArray(filters[filter])
      ? filters[filter].map((f) => f.value || f)
      : filters[filter]
    return acc
  }, {})
  return mutatedFilters
}

const selectData = (state) => state.investigation.self.data
const selectLoader = (state) => state.investigation.self.loader
const selectFiltersLoader = (state) => state.investigation.self.filtersLoader
const selectError = (state) => state.investigation.self.error
const selectFilters = (state) => state.investigation.self.filters
const selectPageSizeDirect = (state) => state.investigation.self.pageSize
const selectPageSize = createSelector(selectPageSizeDirect, (pageSize) => pageSize)
const selectMutatedFilters = createSelector(selectFilters, mutateFiltersToOptions)
const selectSelectedFilters = (state) => state.investigation.self.selectedFilters

const selectMutatedSelectedFilters = createSelector(selectSelectedFilters, (selectedFilters) =>
  mutateFiltersToLabels(selectedFilters)
)
const selectPermissionsError = (state) => state.investigation.self.permissionsError

const selectLogDateFilter = (state) => state.investigation.self.logDateFilter
const selectLogDateRangeDummy = (state) => state.investigation.self.logDateRange

const selectArrivalDateFilter = (state) => state.investigation.self.emailDateFilter
const selectArrivalDateRange = (state) => state.investigation.self.emailDateRange

const selectSavedFilters = (state) => state.investigation.self.savedFilters
const selectSavedFiltersOrder = (state) => state.investigation.self.savedFiltersOrder
const selectSavedFiltersDefault = (state) => state.investigation.self.defaultFilter
const selectSavedFiltersArr = createSelector(
  selectSavedFilters,
  selectSavedFiltersOrder,
  selectSavedFiltersDefault,
  (savedFilters, order, defaultFilter) =>
    order
      ?.map((id) => savedFilters[id])
      .sort((a, b) => {
        if (a.presetName === defaultFilter.presetName) return -1
        if (b.presetName === defaultFilter.presetName) return 1
        return a.presetName < b.presetName ? -1 : 1
      })
)

export const selectSavedSelectedFilterId = (state) => state.investigation.self.selectedFilter

export const selectSavedSelectedFilter = createSelector(
  selectSavedFilters,
  selectSavedSelectedFilterId,
  (savedFilters, selectedFilterId) => savedFilters?.[selectedFilterId]
)

const selectPage = (state) => state.investigation.self.page

const selectAllFilters = createSelector(
  [selectSelectedFilters, selectLogDateFilter, selectArrivalDateFilter],
  (selectedFilters, logDateFilter, emailDateFilter) => ({
    ...selectedFilters,
    logDateFilter,
    emailDateFilter,
  })
)

const selectHasThreatLogPermissions = createSelector(
  selectActualLicenses,
  selectActualFeatureFlags,
  selectActualPermissions,
  (licenses, ff, permissions) => {
    const requiredPermissions = permManager.check(permissions, [
      'admin',
      'irontraps_action',
      'manage_irontraps',
      'irontraps_viewer',
    ])
    return (
      atLeastIronscalesProtectPlan(licenses.plan) &&
      !ff.DISABLE_INVESTIGATION_TAB &&
      requiredPermissions
    )
  }
)

const selectHasCreateIncidentPermissions = createSelector(
  selectActualLicenses,
  selectActualFeatureFlags,
  selectActualPermissions,
  (licenses, ff, permissions) => {
    const requiredPermissions = permManager.check(permissions, [
      'admin',
      'irontraps_action',
      'manage_irontraps',
    ])
    return (
      atLeastIronscalesProtectPlan(licenses.plan) &&
      !ff.DISABLE_INVESTIGATION_TAB &&
      requiredPermissions
    )
  }
)

const selectMappings = (state) => state.investigation.self.mappings
const selectDefaultFilters = (state) => state.investigation.self.defaultFilters

const selectThreatLogCompanies = createSelector(
  selectSortedCompaniesOptions,
  selectPartner,
  selectSelectedFilters,
  selectCompanySelectorObject,
  (companies, partner, selectedFilters, selfCompanySelector) => {
    let selectedCompanies = [...companies]
    const selectedPresumedSafeOption = selectedFilters?.log_type?.includes('Presumed Safe')

    if (partner && !companies.includes(ALL_COMPANIES_OPTION) && !selectedPresumedSafeOption) {
      selectedCompanies.unshift(ALL_COMPANIES_OPTION)
    }
    // FIXME https://ironscales.atlassian.net/browse/IC-32613?focusedCommentId=89051
    if (partner && selectedPresumedSafeOption && companies.includes(ALL_COMPANIES_OPTION)) {
      selectedCompanies = selectedCompanies.filter(
        (company) => company.label === selfCompanySelector.label
      )
    }
    return selectedCompanies
  }
)

const selectEmailDateRange = createSelector(
  selectArrivalDateRange,
  selectArrivalDateFilter,
  (range, filter) => range || getRangeFromDateFilter(filter)
)

const selectLogDateRange = createSelector(
  selectLogDateRangeDummy,
  selectLogDateFilter,
  (range, filter) => range || getRangeFromDateFilter(filter)
)

const selectThreatLogFilters = createSelector(
  selectLogDateRange,
  selectEmailDateRange,
  selectMutatedSelectedFilters,
  selectCompanyFilter,
  selectSelfCompanyId,
  selectPartner,
  selectPage,
  selectPageSize,
  (
    logDateRange,
    emailDateRange,
    filters,
    companyFilter,
    selfCompanyId,
    partner,
    page,
    pageSize
  ) => ({
    ...filters,
    logDateRange,
    emailDateRange,
    company_ids: prepareCompanyIds(partner, companyFilter, selfCompanyId),
    page_number: page,
    page_size: pageSize,
    is_scanback_report: prepareBool(filters.is_scanback_report),
    silent_mode: prepareBool(filters.silent_mode),
    has_incident: prepareBool(filters.has_incident),
    spam_email_released: prepareBool(filters.spam_email_released),
    vip: prepareBool(filters.vip),
  })
)

const selectFilteredThreatLogFilters = createSelector(selectThreatLogFilters, (filters) => {
  const { emailDateFilter, logDateFilter, ...filteredData } = filters
  return filteredData
})

const selectIocAccessPermission = createSelector(
  selectActualFeatureFlags,
  (ff) => !ff.HIDE_PRESUMED_SAFE
)

const selectMappingsError = (state) => state.investigation.self.mappingsError

const selectTotalHits = (state) => state.investigation.self.total

const selectGreaterThenMaxTotal = (state) => state.investigation.self.greaterThenMaxTotal

const selectLogsEntireCount = (state) => state.investigation.self.logsEntireCount

const selectMitigationData = (state) => state.investigation.self.mitigation

const selectFetching = (state) => state.investigation.self.fetching

const selectIsPresetsLoading = (state) => state.investigation.self.isPresetsLoading

const selectFailedEmail = (state) => state.investigation.self.failedEmail
const selectSelectedLogsMap = (state) => state.investigation.self.selectedLogsMap
const selectExcludedLogsMap = (state) => state.investigation.self.excludedLogsMap
const selectSelectAllStatus = (state) => state.investigation.self.selectAllStatus

const selectIsTableSelectable = createSelector(
  selectActualIntegrations,
  selectActualFeatureFlags,
  selectAllFilters,
  selectCompanyFilter,
  (integrations, ff, allFilters, companyFilter) =>
    ff.ENABLE_SPAM_FILTER &&
    integrations.o365 &&
    allFilters.log_type?.includes('Spam email detected') &&
    allFilters.spam_email_released?.every((item) => item === false) &&
    allFilters.has_incident?.every((item) => item === false) &&
    companyFilter?.label !== ALL_COMPANIES_OPTION.label
)

const selectReleaseFromSpamEnabled = createSelector(
  selectSelectedLogsMap,
  selectSelectAllStatus,
  selectIsStaticSalesDemo,
  selectExcludedLogsMap,
  selectTotalHits,
  (selectedLogsMap, selectAllStatus, isStaticSalesDemo, excludedLogsMap, totalHits) => {
    const selectedLogsCount = Object.keys(selectedLogsMap).filter(
      (key) => selectedLogsMap[key]
    ).length

    if (selectAllStatus) {
      const excludedCount = Object.keys(excludedLogsMap).filter(
        (key) => excludedLogsMap[key]
      ).length
      return totalHits - excludedCount > 0 && !isStaticSalesDemo
    }
    return selectedLogsCount > 1 && !isStaticSalesDemo
  }
)

const selectReleaseFromSpamData = createSelector(
  selectSelectedLogsMap,
  selectExcludedLogsMap,
  selectSelectAllStatus,
  selectFilteredThreatLogFilters,
  (selectedLogsMap, excludedLogsMap, selectAllStatus, filteredFilters) => {
    let body = {}
    if (selectAllStatus) {
      body = {
        ...filteredFilters,
        excluded_arr: Object.values(
          Object.entries(excludedLogsMap).reduce((acc, [key, value]) => {
            if (value !== false) acc[key] = value
            return acc
          }, {})
        ),
        selectAllStatus,
      }
    } else {
      body = {
        selectAllStatus,
        included_arr: Object.values(
          Object.entries(selectedLogsMap).reduce((acc, [key, value]) => {
            if (value !== false) acc[key] = value
            return acc
          }, {})
        ),
      }
    }
    return body
  }
)

const selectAllSelectedFromPage = (state) => {
  const { selectedLogsMap, excludedLogsMap, selectAllStatus, data } = state.investigation.self

  if (selectAllStatus) {
    const excludedIds = data.filter(({ id }) => excludedLogsMap[id])

    return !!data.length && !excludedIds.length
  }

  const selectedIds = data.filter(({ id }) => selectedLogsMap[id])
  return !!data.length && selectedIds.length === data.length
}
const selectFetchingLogs = (state) => state.investigation.self.fetchingLogs

const selectDirectoryTags = (state) => state.investigation.self.tags
const selectDirectoryTagsLoader = (state) => state.investigation.self.tagsLoader
const selectDirectoryTagsSearch = (state) => state.investigation.self.tagsSearch
const selectDirectoryTagsPage = (state) => state.investigation.self.tagsPage
const selectDirectoryTagsTotalPages = (state) => state.investigation.self.totalPages
const selectSelectedDirectoryTags = createSelector(
  selectSelectedFilters,
  (selectedFilters) => selectedFilters.tagIds
)

export {
  selectData,
  selectMappings,
  selectPage,
  selectPageSize,
  selectLoader,
  selectFiltersLoader,
  selectError,
  selectFilters,
  selectMutatedFilters,
  selectMutatedSelectedFilters,
  selectSelectedFilters,
  selectLogDateFilter,
  selectArrivalDateFilter,
  selectHasThreatLogPermissions,
  selectThreatLogFilters,
  selectDefaultFilters,
  selectPermissionsError,
  selectThreatLogCompanies,
  selectIocAccessPermission,
  selectMappingsError,
  selectHasCreateIncidentPermissions,
  selectTotalHits,
  selectGreaterThenMaxTotal,
  selectLogsEntireCount,
  selectMitigationData,
  selectAllFilters,
  selectSavedFiltersArr,
  selectSavedFilters,
  selectFetching,
  selectEmailDateRange,
  selectLogDateRange,
  selectIsPresetsLoading,
  selectFilteredThreatLogFilters,
  selectFailedEmail,
  selectSelectedLogsMap,
  selectExcludedLogsMap,
  selectIsTableSelectable,
  selectSelectAllStatus,
  selectReleaseFromSpamEnabled,
  selectReleaseFromSpamData,
  selectAllSelectedFromPage,
  selectFetchingLogs,
  selectDirectoryTags,
  selectDirectoryTagsLoader,
  selectDirectoryTagsSearch,
  selectDirectoryTagsPage,
  selectSelectedDirectoryTags,
  selectDirectoryTagsTotalPages,
}
